/**
 * 用户行为埋点 Vue 插件
 *
 * 功能：
 * - 自动路由埋点（页面浏览）
 * - 提供 $track 方法供组件调用
 * - 提供 v-track 指令用于点击事件埋点
 */

import { trackPageView, trackClick, setCurrentPage } from '@/utils/analytics'

// ========== 路由到页面标识的映射 ==========

// 路由名称映射（将路由名称转换为页面标识）
const routeToPageMap = {
  'TechDetail': 'partner_detail',
  'TechPortfolio': 'partner_portfolio',
  'TechReview': 'partner_review',
  'TechReviews': 'partner_reviews',
  'CreateReservation': 'booking_booking',
  'OrderList': 'booking_list',
  'OrderDetail': 'booking_detail',
  'OrderExtend': 'booking_extend',
  'ChatList': 'chat_list',
  'MessageCenter': 'message_center',
  'MessageList': 'message_list',
  'PortfolioDetail': 'portfolio_detail',
  'PortfolioTopic': 'portfolio_topic',
  'CityList': 'city_list',
  'Login': 'login',
  'MobileLogin': 'mobile_login',
  'VipPurchase': 'vip_purchase'
}

// 路径映射（将路径转换为页面标识，用于没有name的路由）
const pathToPageMap = {
  '/': 'home',
  '/splash': 'splash',
  '/recommended': 'home',
  '/program': 'program',
  '/coaches': 'partner_list',
  '/coach-detail': 'partner_detail',
  '/conversations': 'chat_list',
  '/conversation-detail': 'chat_message',
  '/video': 'video',
  '/messages/center': 'message_center',
  '/messages/list': 'message_list',
  '/profile': 'my',
  '/wellness': 'wellness',
  '/portfolio/detail': 'portfolio_detail',
  '/portfolio/topics': 'portfolio_topic',
  '/login': 'login',
  '/mobile-login': 'mobile_login',
  '/cities': 'city_list'
}

/**
 * 将路由名称转换为页面标识
 * @param {Object} route - Vue路由对象
 * @returns {string} 页面标识
 */
function routeNameToPage(route) {
  const routeName = route.name
  const routePath = route.path

  // 优先使用路由的 meta.title
  if (route.meta && route.meta.title) {
    return route.meta.title
  }

  // 使用路由名称映射
  if (routeName && routeToPageMap[routeName]) {
    return routeToPageMap[routeName]
  }

  // 如果有路由名称，转换格式
  if (routeName) {
    return routeName.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
  }

  // 使用路径映射
  if (routePath && pathToPageMap[routePath]) {
    return pathToPageMap[routePath]
  }

  // 路径转换（移除开头的斜杠，将斜杠替换为下划线，移除数字）
  if (routePath) {
    return routePath
      .replace(/^\//, '')
      .replace(/\/\d+/g, '')  // 移除路径中的数字
      .replace(/\//g, '_')
      .replace(/-/g, '_')
      .toLowerCase()
  }

  return 'unknown'
}

/**
 * 从路由中提取业务扩展信息
 * @param {Object} route - Vue路由对象
 * @returns {Object} 业务扩展信息
 */
function extractBizExtFromRoute(route) {
  const bizExt = {}
  const params = route.params || {}
  const query = route.query || {}

  // 根据不同页面提取业务信息
  switch (route.name) {
    case 'partner-detail':
      if (params.id || query.id) {
        bizExt.refType = 'partner'
        bizExt.refId = Number(params.id || query.id)
      }
      break

    case 'booking-detail':
      if (params.id || query.id) {
        bizExt.refType = 'booking'
        bizExt.refId = Number(params.id || query.id)
      }
      if (query.bookingNo) {
        bizExt.bookingNo = query.bookingNo
      }
      break

    case 'package-detail':
      if (params.id || query.id) {
        bizExt.refType = 'package'
        bizExt.refId = Number(params.id || query.id)
      }
      break

    case 'portfolio-detail':
      if (params.id || query.id) {
        bizExt.refType = 'portfolio'
        bizExt.refId = Number(params.id || query.id)
      }
      break

    case 'webview':
      if (query.url) {
        bizExt.webviewUrl = query.url
      }
      break
  }

  return bizExt
}

/**
 * Vue 插件安装方法
 * @param {Object} app - Vue应用实例
 * @param {Object} options - 插件选项
 */
export default {
  install(app, options = {}) {
    const { router, autoTrack = true } = options

    // ========== 添加全局方法 ==========

    app.config.globalProperties.$trackPageView = (page, bizExt) => {
      trackPageView(page, bizExt)
    }

    app.config.globalProperties.$trackClick = (element, bizExt) => {
      trackClick(element, bizExt)
    }

    // ========== 添加全局指令 ==========

    // v-track 指令 - 点击埋点
    app.directive('track', {
      mounted(el, binding) {
        if (!binding.value) return

        const { element, bizExt } = binding.value

        el.addEventListener('click', () => {
          trackClick(element, bizExt)
        }, { passive: true })
      }
    })

    // ========== 自动路由埋点 ==========

    if (autoTrack && router) {
      router.afterEach((to) => {
        // 更新当前页面标识
        const page = routeNameToPage(to)
        setCurrentPage(page)

        // 延迟上报，避免影响路由跳转性能
        setTimeout(() => {
          const bizExt = extractBizExtFromRoute(to)

          // 添加来源信息
          if (to.query.from) {
            bizExt.from = to.query.from
          }

          trackPageView(page, bizExt)
        }, 100)
      })
    }

    console.log('[Analytics Plugin] 埋点插件已安装', { autoTrack })
  }
}

// ========== 导出辅助函数 ==========

/**
 * 手动创建埋点插件实例（用于按需加载）
 * @param {Object} options - 插件选项
 * @returns {Object} 插件对象
 */
export function createAnalyticsPlugin(options) {
  return {
    install(app) {
      install(app, options)
    }
  }
}
